import React, { useEffect, useState } from 'react';
import { openDB } from 'idb';

const THUMBNAIL_VERSION = 'cover-v6';
const TARGET_WIDTH = 960;
const TARGET_HEIGHT = 600;
const TARGET_ASPECT_RATIO = TARGET_WIDTH / TARGET_HEIGHT;
const QUALITY_MULTIPLIER = 2.6;
const TOP_CROP_OFFSET = 0.12;

function ensurePDFJS(timeoutMs = 30000) {
  return new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      resolve(window.pdfjsLib);
    } else {
      const startTime = Date.now();
      const checkPDFJS = () => {
        if (window.pdfjsLib) {
          resolve(window.pdfjsLib);
        } else if (Date.now() - startTime > timeoutMs) {
          reject(new Error('PDF.js failed to load within timeout'));
        } else {
          setTimeout(checkPDFJS, 100);
        }
      };
      checkPDFJS();
    }
  });
}

const PDFThumbnail = ({ pdfId, metadata, className = '', imageClassName = '' }) => {
  const [thumbnail, setThumbnail] = useState(null);
  const [error, setError] = useState(null);
  const cacheKey = `${THUMBNAIL_VERSION}:${pdfId}`;

  useEffect(() => {
    const loadThumbnail = async () => {
      try {
        const db = await openDB('PDFStore', 4);
        const cachedThumbnail = await db.get('thumbnails', cacheKey);

        if (cachedThumbnail) {
          setThumbnail(cachedThumbnail);
        } else {
          const generatedThumbnail = await generateThumbnail(pdfId);
          setThumbnail(generatedThumbnail);
          await db.put('thumbnails', generatedThumbnail, cacheKey);
        }
      } catch (error) {
        console.error('Error loading thumbnail:', error);
        setError(error.message);
      }
    };

    loadThumbnail();
  }, [cacheKey, pdfId]);

  const generateThumbnail = async (pdfId) => {
    try {
      console.log('Starting to load PDF:', pdfId);
      const db = await openDB('PDFStore', 4);
      const pdfBlob = await db.get('pdfs', pdfId);
      if (!pdfBlob) {
        throw new Error('PDF not found in the database');
      }

      const pdfjsLib = await ensurePDFJS();

      const arrayBuffer = await pdfBlob.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({data: arrayBuffer});
      const pdf = await loadingTask.promise;
      console.log('PDF document loaded');

      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1 });
      const displayScale = TARGET_WIDTH / viewport.width;
      const renderScale = displayScale * QUALITY_MULTIPLIER;
      const scaledViewport = page.getViewport({ scale: renderScale });

      const sourceCanvas = document.createElement('canvas');
      const sourceContext = sourceCanvas.getContext('2d');

      sourceCanvas.width = Math.ceil(scaledViewport.width);
      sourceCanvas.height = Math.ceil(scaledViewport.height);

      const renderContext = {
        canvasContext: sourceContext,
        viewport: scaledViewport
      };

      await page.render(renderContext).promise;

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = TARGET_WIDTH;
      canvas.height = TARGET_HEIGHT;
      const cropWidth = sourceCanvas.width;
      const cropHeight = Math.min(sourceCanvas.height, Math.round(cropWidth / TARGET_ASPECT_RATIO));
      const maxCropY = Math.max(0, sourceCanvas.height - cropHeight);
      const cropY = Math.round(maxCropY * TOP_CROP_OFFSET);

      context.fillStyle = '#f8fafc';
      context.fillRect(0, 0, TARGET_WIDTH, TARGET_HEIGHT);
      context.drawImage(
        sourceCanvas,
        0,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        TARGET_WIDTH,
        TARGET_HEIGHT
      );

      const dataUrl = canvas.toDataURL('image/jpeg', 0.82);

      // Cleanup: release canvas memory and destroy PDF document
      sourceCanvas.width = 0;
      sourceCanvas.height = 0;
      canvas.width = 0;
      canvas.height = 0;
      pdf.destroy();

      return dataUrl;
    } catch (error) {
      console.error('Error generating PDF thumbnail:', error);
      throw error;
    }
  };

  if (error) {
    return (
      <div className={`flex h-full w-full items-center justify-center rounded-[18px] bg-red-100 p-2 text-red-700 ${className}`}>
        <span className="text-sm">Error loading thumbnail: {error}</span>
      </div>
    );
  }

  return (
    <div className={`h-full w-full overflow-hidden ${className}`}>
      {thumbnail ? (
        <img 
          src={thumbnail} 
          alt="PDF thumbnail" 
          className={`h-full w-full object-cover object-top ${imageClassName}`}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <span className="text-gray-400">Loading...</span>
        </div>
      )}
    </div>
  );
};

export default PDFThumbnail;
